import { PERMISSIONS } from "@shared/constants/permissions";
import RemoveGhostIcon from "@shared/svg/remove-ghost.svg?react";
import type { Asset } from "@shared/types/asset";
import { AlertBox } from "@shared/ui/AlertBox";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import { SearchInput, SEARCH_INPUT_SIZES } from "@shared/ui/Inputs";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import { Plus } from "phosphor-react";
import React from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Waypoint } from "react-waypoint";

import getEnums from "$/settings/enums";
import PAID_FEATURES from "$/settings/paid-features.json";
import AssetRow from "~/components/assets/AssetRow";
import AssignAssetsToParent from "~/components/assets/AssignAssetsToParent";
import { useAuth } from "~/components/general";
import { ITEMS_BEFORE_PAGE, ITEMS_PER_PAGE } from "~/constants";
import usePermissions from "~/hooks/usePermissions";
import { NoTicketsLargeIcon } from "~/icons/icons";
import { useUnassignAssetFromParent } from "~/services/asset/mutation";
import { useListAllOwnOemAssets } from "~/services/asset/query";
import { UserType } from "~/types/user";
import { WaypointEventType } from "~/types/waypointEvent";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

const paidFeatures = getEnums(PAID_FEATURES, "reference");

const SubAssets = ({
  asset,
  isSharedAssetsPage = false,
  sharedAssetsOrg = "",
  isQRCodeEnabled = true,
  handleQRCodeClick = () => {},
  /** Optional callback for asset click. When provided, replaces default Link navigation. */
  onAssetClick,
  showActionsDropdown = false,
  portalConnectionId = "",
}: {
  asset: Asset;
  isSharedAssetsPage?: boolean;
  sharedAssetsOrg?: string;
  isQRCodeEnabled?: boolean;
  handleQRCodeClick?: (asset: Asset) => void;
  onAssetClick?: (assetId: string, assetName: string) => void;
  showActionsDropdown?: boolean;
  /**
   * The portal this asset is being viewed through. When set, sub-assets are
   * read through the portal's access-gated query, which authorizes the listing
   * by walking up to a shared ANCESTOR. The OEM query instead requires the
   * caller to be a contact of the parent asset's own company, which is never
   * true for an asset reshared down the portal hierarchy — it still belongs to
   * the company that shared it down.
   */
  portalConnectionId?: string;
}) => {
  const { messages } = useIntl();
  const [showAssignAsset, setShowAssignAsset] = React.useState(false);
  const [subAssetSearchQuery, setSubAssetSearchQuery] = React.useState("");
  const [assignAssetSearchQuery, setAssignAssetSearchQuery] =
    React.useState("");
  const [showAlert, setShowAlert] = React.useState(false);

  const {
    assets: allSubAssets,
    assetsListLoading: allSubAssetsLoading,
    handleFetchMore: handleFetchMoreSubAssets,
    totalCount: allSubAssetsTotalCount,
  } = useListAllOwnOemAssets({
    where: {
      ...(subAssetSearchQuery
        ? { parentId: asset._id }
        : { directParentId: asset._id }),
      isSharedAsset: isSharedAssetsPage,
      ...(sharedAssetsOrg ? { oemId: sharedAssetsOrg } : {}),
      // The portal query resolves access from this connection, so it is
      // required rather than merely a filter.
      ...(portalConnectionId ? { customer: portalConnectionId } : {}),
    },
    searchQuery: subAssetSearchQuery,
    limit: ITEMS_PER_PAGE,
    skip: 0,
    useCustomerPortalQuery: !!portalConnectionId,
  });
  const {
    assets: allAssets,
    assetsListLoading: allAssetsLoading,
    handleFetchMore: handleFetchMoreAssets,
    totalCount: allAssetsTotalCount,
  } = useListAllOwnOemAssets({
    where: {
      filterAssetAssigned: false,
      id_nin: [asset._id, ...(asset.hierarchy?.map((item) => item.id) || [])],
      ...(sharedAssetsOrg ? { oemId: sharedAssetsOrg } : {}),
    },
    searchQuery: assignAssetSearchQuery,
    limit: ITEMS_PER_PAGE,
    skip: 0,
    // The picker this feeds is only reachable when the assign button renders,
    // which a shared/portal page never does.
    skipCondition: isSharedAssetsPage,
  });

  const { mutation: unassignAsset } = useUnassignAssetFromParent();

  const selectedAsset = React.useRef(null);

  const { user } = useAuth() as { user: UserType };
  const { canChange } = usePermissions();
  const canUpdateAssets = canChange(
    PERMISSIONS.assets.update,
    asset?.createdBy,
  );
  const appConfig = useGetAppConfigValue();
  const isTeamsPaid = isPaidFeatureAvailable(
    paidFeatures.teams,
    user,
    appConfig,
  );
  const isHierarchyPaid = isPaidFeatureAvailable(
    paidFeatures.hierarchy,
    user,
    appConfig,
  );

  const handleScrollBottom = (event: WaypointEventType) => {
    if (
      allSubAssets.length >= ITEMS_PER_PAGE &&
      !allSubAssetsLoading &&
      event?.previousPosition !== Waypoint.above
    ) {
      handleFetchMoreSubAssets({
        limit: ITEMS_PER_PAGE,
        skip: allSubAssets.length,
      });
    }
  };

  const handleScrollBottomAllAssets = (event: WaypointEventType) => {
    if (
      allAssets.length >= ITEMS_PER_PAGE &&
      !allAssetsLoading &&
      event?.previousPosition !== Waypoint.above &&
      allAssetsTotalCount > allAssets.length
    ) {
      handleFetchMoreAssets({
        limit: ITEMS_PER_PAGE,
        skip: allAssets.length,
      });
    }
  };

  const handleSearch = (value: string) => {
    setSubAssetSearchQuery(value);
  };

  const handleUnassignClick = (assetId: string) => {
    selectedAsset.current = assetId;
    setShowAlert(true);
  };

  const handleUnassignCancel = () => {
    setShowAlert(false);
    selectedAsset.current = null;
  };

  const handleUnassignAccept = () => {
    setShowAlert(false);
    try {
      unassignAsset({
        assetId: selectedAsset.current,
        parentId: asset._id,
      });
      toast(
        <Toast
          message={messages["hierarchy"]["assets"].unassign.successMessage}
        />,
        {
          closeButton: false,
        },
      );
    } catch (error) {
      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={
            error?.message ||
            messages["hierarchy"]["assets"].unassign.failureMessage
          }
        />,
        {
          closeButton: false,
        },
      );
    } finally {
      selectedAsset.current = null;
    }
  };

  return (
    <>
      <div
        className={`flex items-center mb-md -mt-md ${
          !isHierarchyPaid || !canUpdateAssets
            ? "justify-end"
            : "justify-between"
        }`}
      >
        {isHierarchyPaid && canUpdateAssets && !isSharedAssetsPage && (
          <Button
            variant={BUTTON_VARIANTS.LINK}
            text={messages["hierarchy"]["assets"].assign.linkButtonText}
            // @ts-ignore
            leadingIcon={<Plus size={14} weight="bold" />}
            onClick={() => {
              setShowAssignAsset(true);
            }}
          />
        )}

        <SearchInput
          onChange={handleSearch}
          value={subAssetSearchQuery}
          placeholder={
            messages["hierarchy"]["assets"].assign.subAssetsSearchPlaceholder
          }
        />
      </div>

      {(!asset?.childrenCount ||
        (subAssetSearchQuery && !allSubAssets?.length)) && (
        <div className="flex w-full flex-col items-center justify-center gap-y-lg text-center min-h-[calc(100vh-400px)]">
          <NoTicketsLargeIcon width="140px" height="140px" />
          <BodyText
            size={BODY_TEXT_SIZES.MEDIUM}
            color="text-secondary"
            className="max-w-[300px] mt-lg"
          >
            {asset?.childrenCount &&
            subAssetSearchQuery &&
            !allSubAssets?.length
              ? messages["common"]["noResultFound"]
              : messages?.hierarchy?.["assets"]?.assetView
                  ?.noSubAssetsDescription}
          </BodyText>
        </div>
      )}

      {!allSubAssetsLoading && !!allSubAssets?.length && (
        <div className="flex flex-col gap-y-sm">
          {allSubAssets?.map((subAsset: Asset, index: number) => (
            <React.Fragment key={subAsset._id}>
              <AssetRow
                asset={subAsset}
                handleUnassignClick={
                  canUpdateAssets
                    ? () => handleUnassignClick(subAsset._id)
                    : undefined
                }
                isAssetQuery={!!subAssetSearchQuery}
                handleQRCodeClick={(asset) => handleQRCodeClick(asset)}
                isHierarchyPaid={isSharedAssetsPage ? false : isHierarchyPaid}
                // Teams are an OEM-internal assignment, so the tag stays out of
                // the portal — as the OEM's own portal-assets row already does.
                isTeamsPaid={isSharedAssetsPage ? false : isTeamsPaid}
                isSharedAssetPage={isSharedAssetsPage}
                showQRCode={isQRCodeEnabled}
                onAssetClick={onAssetClick}
                showActionsDropdown={showActionsDropdown}
                portalConnectionId={portalConnectionId}
                sharedAssetsOrg={sharedAssetsOrg}
              />

              {subAssetSearchQuery &&
                !allSubAssetsLoading &&
                allSubAssetsTotalCount > allSubAssets.length &&
                index === allSubAssets.length - ITEMS_BEFORE_PAGE && (
                  // @ts-ignore
                  <Waypoint onEnter={handleScrollBottom} />
                )}
            </React.Fragment>
          ))}
        </div>
      )}

      <AssignAssetsToParent
        isOpen={showAssignAsset}
        allAssets={allAssets}
        handleClose={() => setShowAssignAsset(false)}
        handleScrollBottomAllAssets={handleScrollBottomAllAssets}
        allAssetsLoading={allAssetsLoading}
        searchQuery={assignAssetSearchQuery}
        setSearchQuery={setAssignAssetSearchQuery}
      />
      <AlertBox
        title={messages["hierarchy"]["assets"].unassign.alertTitle}
        isOpen={showAlert}
        cancelButtonText={
          messages["facilities"]["facilityDetails"].machinesTab.edit.changes
            .unassignWindow.no
        }
        acceptButtonText={
          messages["facilities"]["facilityDetails"].machinesTab.edit.changes
            .unassignWindow.yes
        }
        image={<RemoveGhostIcon width="130" height="130" />}
        onCancel={handleUnassignCancel}
        onAccept={handleUnassignAccept}
        overlay
      />
    </>
  );
};

export default SubAssets;
